import React, { useMemo, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import MarketingLayout from "components/layout/MarketingLayout";
import { Button, Input, Textarea } from "components/ui";
import { contactUsService } from "services/contactus.service";
import { ContactUs } from "@/types/contactus";
import { useFieldValidation } from "hooks/useFieldValidation";
import { RECAPTCHA_SITE_KEY } from "constants/index";
import supportIllustration from "assets/images/website/teams-collab.svg";
import { set } from "react-hook-form";

const ContactPage: React.FC = () => {
  const [sent, setSent] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  type ContactFormState = {
    name: string;
    email: string;
    message: string;
  };

  const validators = useMemo(
    () => ({
      name: (state: ContactFormState) =>
        !state.name?.trim() ? "Please enter your name" : undefined,
      message: (state: ContactFormState) =>
        !state.message?.replace("<p><br></p>", "").trim() ? "Please enter your message" : undefined,
      email: (state: ContactFormState) =>
        !state.email?.trim() ? "Please enter your email" : undefined,
    }),
    []
  );

  const { errors, validateAll, revalidateFields, resetErrors } =
    useFieldValidation<ContactFormState>(validators);

  const [form, setForm] = useState<ContactUs>({
    id: "0",
    name: "",
    email: "",
    message: "",
    companyName: "",
    captchaToken: ""
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationResult = validateAll(form);
    if (Object.keys(validationResult).length > 0) {
      return;
    }

    // Execute reCAPTCHA v3
    if (!executeRecaptcha) {
      alert("reCAPTCHA is not loaded. Please try again.");
      return;
    }

    try {
      const token = await executeRecaptcha("contact_form");
      const payload = {...form, captchaToken: token};
      await contactUsService.create(payload);
      setSent(true);
    } catch (error) {
      console.error("reCAPTCHA error:", error);
      alert("Failed to verify reCAPTCHA. Please try again.");
    }
  };

  return (
    <MarketingLayout>
      {/* Background accents */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-br from-primary-200/40 to-teal-200/30 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-gradient-to-tr from-primary-100/40 to-secondary-200/30 blur-3xl" />
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-14 pb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary-500 to-teal-400 text-white grid place-items-center shadow-lg">
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 15a2 2 0 01-2 2h-3l-4 4v-4H7a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v9z"
                />
              </svg>
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-secondary-900">
              We’d love to hear from you
            </h1>
          </div>
          <p className="mt-2 text-secondary-700 max-w-2xl">
            Questions, feedback, or partnership ideas—our team typically replies
            within one business day.
          </p>
        </div>
      </section>

      {/* Form + Info */}
      <section>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-16 grid lg:grid-cols-1 gap-10 items-stretch place-items-center">
          {/* Left: Form */}
          <div className="relative max-w-xl   w-full">
            <div className="absolute -inset-0.5 -z-10 rounded-3xl bg-gradient-to-br from-primary-300/20 to-teal-300/20 blur-xl" />
            <div className="rounded-3xl border border-secondary-200 bg-white/80 backdrop-blur p-6 sm:p-8 shadow-xl">
              {sent ? (
                <div className="p-5 rounded-xl bg-gradient-to-r from-primary-50 to-teal-50 text-secondary-900 border border-primary-200/50 transition">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-teal-500 text-white grid place-items-center">
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div>
                      <div className="font-semibold">Message sent!</div>
                      <div className="text-secondary-700">
                        We’ll get back to you shortly.
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-4">
                  <Input
                    name="name"
                    placeholder="Your name"
                    error={errors.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      setForm((prevData) => ({
                        ...prevData,
                        name: e.target.value,
                      }));
                    }}
                    leftIcon={
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    }
                  />
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email address"
                    error={errors.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      setForm((prevData) => ({
                        ...prevData,
                        email: e.target.value,
                      }));
                    }}
                    leftIcon={
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    }
                  />
                  <Input
                    name="company"
                    placeholder="Company (optional)"
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      setForm((prevData) => ({
                        ...prevData,
                        companyName: e.target.value,
                      }));
                    }}
                    leftIcon={
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 21h18M9 8h6M4 21V7a2 2 0 012-2h12a2 2 0 012 2v14"
                        />
                      </svg>
                    }
                  />
                  <Textarea
                    name="message"
                    placeholder="How can we help?"
                    error={errors.message}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      setForm((prevData) => ({
                        ...prevData,
                        message: e.target.value,
                      }));
                    }}
                    rows={6}
                  />

                  <Button
                    type="submit"
                    className="rounded-full bg-gradient-to-r from-primary-600 to-teal-500 hover:from-primary-700 hover:to-teal-600 transition-transform hover:-translate-y-0.5"
                  >
                    Send message
                  </Button>
                </form>
              )}
            </div>
          </div>

          {/* Right: Illustration + Info (Restored commented areas) */}
          {/* <aside className="relative overflow-hidden rounded-3xl border border-secondary-200 bg-white shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-b from-white to-secondary-50/60" />
            <div className="relative p-6 sm:p-8">
              <img src={supportIllustration} alt="Support" className="w-full h-48 object-contain" />
              <div className="mt-6 grid gap-4">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-xl bg-primary-100 text-primary-700 grid place-items-center">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 12l4.243-4.243m-11.314 0L10.586 12 6.343 16.243"/></svg>
                  </div>
                  <div>
                    <div className="font-semibold text-secondary-900">Location</div>
                    <div className="text-secondary-700">Beirut, Lebanon</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-xl bg-primary-100 text-primary-700 grid place-items-center">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                  </div>
                  <div>
                    <div className="font-semibold text-secondary-900">Email</div>
                    <div className="text-secondary-700">support@qcard.app</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-xl bg-primary-100 text-primary-700 grid place-items-center">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3"/></svg>
                  </div>
                  <div>
                    <div className="font-semibold text-secondary-900">Support hours</div>
                    <div className="text-secondary-700">Mon–Fri, 9am–6pm (EET)</div>
                  </div>
                </div>
              </div>

              {/* Map preview placeholder */}
              {/* <div className="mt-6"> 
                <div className="relative h-40 rounded-xl border border-secondary-200 overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-secondary-100 via-white to-secondary-100" />
                  <div className="relative z-10 h-full w-full grid place-items-center text-secondary-600 text-sm">Beirut map preview</div>
                </div>
              </div>
            </div>
          </aside> */}
        </div>
      </section>
    </MarketingLayout>
  );
};

export default ContactPage;